<template>
  <div class="relative flex mb-3 justify-start">
    <div
      class="absolute -left-3 -top-3 w-7 z-10 overflow-hidden z-10"
      style="display: flex; justify-content: center; align-items: center; padding: 1px;"
    >
      <img :src="avatar" alt="Scout" class="max-w-full max-h-full object-contain" />
    </div>

    <div class="w-[90%] ml-2">
      <details ref="detailsElement" class="group" @toggle="handleToggle">
        <summary
          class="cursor-pointer p-2 bg-gpx_gray-900 hover:bg-gpx_gray-800 transition-colors rounded-xl"
          :class="isOpen ? 'rounded-b-none' : ''"
        >
          <div class="flex justify-between items-center">
            <div class="font-medium flex items-center ">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1.5 text-gpx_gray-200 transition-transform" :class="isOpen ? 'rotate-180' : ''" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
              <span class="text-gpx_slate-100">{{ getHeadingText() }}</span>
              <span v-if="isThinking" class="ml-1.5 inline-block">
                <span class="animate-pulse inline-block h-1.5 w-1.5 rounded-full bg-gpx_gray-200 mr-0.5"></span>
                <span class="animate-pulse inline-block h-1.5 w-1.5 rounded-full bg-gpx_gray-200 mr-0.5" style="animation-delay: 0.2s"></span>
                <span class="animate-pulse inline-block h-1.5 w-1.5 rounded-full bg-gpx_gray-200" style="animation-delay: 0.4s"></span>
              </span>
            </div>
            <div class="text-xs opacity-50 text-gpx_gray-200">
              {{ formatTime(messages[messages.length - 1].created_at) }}
            </div>
          </div>

          <div v-if="getPreviewText(messages)" class="text-xs text-gpx_gray-200 italic mt-1.5 truncate">
            {{ getPreviewText(messages) }}
          </div>
        </summary>

        <div class="bg-gpx_gray-900 rounded-b-xl">
          <div
            v-for="(message, messageIndex) in messages"
            :key="message.id"
            class="p-2.5 relative ai-dialog overflow-x-auto"
            :class="{'border-t border-gpx_gray-900': messageIndex > 0}"
          >
            <MessageContent :content="message.content" :id="message.id" />

            <!-- Message timestamp -->
            <div class="text-xs opacity-50 mt-1 text-right text-gpx_gray-200">
              {{ formatTime(message.created_at) }}
            </div>
          </div>
        </div>
      </details>
    </div>
  </div>
</template>

<script setup>
import MessageContent from './MessageContent.vue';
import { format, fromUnixTime, intervalToDuration, formatDuration } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { ref, onMounted, computed } from 'vue';

const props = defineProps({
  messages: {
    type: Array,
    required: true
  },
  avatar: {
    type: String,
    required: true
  },
  isComplete: {
    type: Boolean,
    default: false
  }
});

const detailsElement = ref(null);
const isOpen = ref(false);

const isThinking = computed(() => !props.isComplete);


function handleToggle() {
  if (detailsElement.value) {
    isOpen.value = detailsElement.value.open;
  }
}

onMounted(() => {
  if (detailsElement.value) {
    isOpen.value = detailsElement.value.open;
  }
});

// Get heading text with thinking time if available
function getHeadingText() {
  if (isThinking.value) {
    return 'Thinking';
  }

  const thinkingTime = getThinkingTime(props.messages);
  return thinkingTime ? `Thought ${thinkingTime}` : 'Thought...';
}

// Format time using date-fns
function formatTime(timestamp) {
  if (!timestamp) return '';

  try {
    // Convert UNIX timestamp to Date object using date-fns
    const date = fromUnixTime(timestamp);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return '';
    }

    // Format time using date-fns format function with US locale
    return format(date, 'h:mm a', { locale: enUS });
  } catch (error) {
    console.error('Error formatting time:', error);
    return '';
  }
}

// Get thinking time between first and last message using date-fns
function getThinkingTime(messages) {
  if (!messages || messages.length < 2) return '';

  const firstTimestamp = messages[0].created_at;
  const lastTimestamp = messages[messages.length - 1].created_at;

  if (!firstTimestamp || !lastTimestamp) return '';

  try {
    const startDate = fromUnixTime(firstTimestamp);
    const endDate = fromUnixTime(lastTimestamp);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return '';
    }

    const diffInMs = endDate.getTime() - startDate.getTime();

    if (diffInMs < 1000) {
      return 'for less than 1 second';
    }

    const duration = intervalToDuration({ start: startDate, end: endDate });

    const filteredDuration = {
      minutes: duration.minutes || 0,
      seconds: duration.seconds || 0
    };

    const format = ['minutes', 'seconds'];

    if (filteredDuration.minutes === 0) {
      format.shift();
    }

    if (filteredDuration.seconds === 0 && filteredDuration.minutes > 0) {
      format.pop();
    }

    const durationText = formatDuration(filteredDuration, {
      format,
      locale: enUS,
      delimiter: ', '
    });

    return durationText ? `for ${durationText}` : '';
  } catch (error) {
    console.error('Error calculating thinking time with date-fns:', error);
    return '';
  }
}

function getPreviewText(messages) {
  if (!messages || !messages.length) return '';

  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];
    if (message?.content) {
      const textBlock = message.content.find(block => block.type === 'text' && block.text);
      if (textBlock && textBlock.text) {
        const text = textBlock.text.trim();
        const maxLength = 60;

        if (text.length <= maxLength) {
          return text;
        }

        const lastSpace = text.lastIndexOf(' ', maxLength);
        const breakpoint = lastSpace > 0 ? lastSpace : maxLength;

        return text.substring(0, breakpoint) + '...';
      }
    }
  }

  return '';
}
</script>

<style>
details > summary {
  list-style: none;
}
details > summary::-webkit-details-marker {
  display: none;
}
</style>
